import api from './api';
import { API_URL } from './api';
import type { AipChartMeta, AirportTopology, ConopsGeoJSON, ImportTopologyResponse } from '../types/conops';
import type { Feature, FeatureCollection } from 'geojson';

export const API_HOST = API_URL.replace('/api/v1', '');

export const conopsService = {
  /**
   * Fetch airport topology (taxiways + stands) for a given ICAO code.
   * Returns taxiways as LineString FeatureCollection and stands as Point FeatureCollection.
   */
  async getTopology(icao: string): Promise<AirportTopology> {
    try {
      const response = await api.get(`/airports/${icao}/topology`);
      return response.data as AirportTopology;
    } catch (error) {
      console.warn(`[conopsService] getTopology failed for ${icao}:`, error);
      return {
        taxiways: { type: 'FeatureCollection', features: [] },
        stands: { type: 'FeatureCollection', features: [] },
        cached: false,
      };
    }
  },

  /**
   * Persist the CONOPS GeoJSON for a scenario.
   */
  async saveConops(scenarioId: string, geojson: ConopsGeoJSON): Promise<void> {
    await api.put(`/simulations/${scenarioId}/conops`, geojson);
  },

  /**
   * Load previously saved CONOPS GeoJSON for a scenario.
   * Returns null if none saved yet.
   */
  async getConops(scenarioId: string): Promise<ConopsGeoJSON | null> {
    try {
      const response = await api.get(`/simulations/${scenarioId}/conops`);
      const data = response.data as ConopsGeoJSON;
      if (!data || !data.features || data.features.length === 0) {
        return null;
      }
      return data;
    } catch (error) {
      console.warn(`[conopsService] getConops failed for scenario ${scenarioId}:`, error);
      return null;
    }
  },

  /**
   * Fetch available AIP AD2 chart images with pre-configured georeferencing corners.
   */
  async getAipCharts(icao: string): Promise<AipChartMeta[]> {
    try {
      const response = await api.get(`/airports/${icao}/aip-charts`);
      return (response.data?.charts ?? []) as AipChartMeta[];
    } catch (error) {
      console.warn(`[conopsService] getAipCharts failed for ${icao}:`, error);
      return [];
    }
  },

  /**
   * Validate a taxi route sequence against the airport topology graph.
   */
  async validateRoute(
    icao: string,
    sequence: string[]
  ): Promise<{ valid: boolean; errors: string[] }> {
    try {
      const response = await api.post(`/airports/${icao}/validate-route`, { sequence });
      return response.data as { valid: boolean; errors: string[] };
    } catch (error) {
      console.warn(`[conopsService] validateRoute failed for ${icao}:`, error);
      return { valid: false, errors: ['Error de conexión al validar la ruta.'] };
    }
  },

  /**
   * Append a single user-drawn taxiway feature to the airport's custom topology.
   * Returns the updated FeatureCollection.
   */
  async saveCustomTaxiway(icao: string, feature: Feature): Promise<FeatureCollection> {
    const response = await api.post(`/airports/${icao}/custom-taxiways`, feature);
    return response.data as FeatureCollection;
  },

  /**
   * Delete a user-drawn taxiway feature by its properties.id value.
   * Returns the updated FeatureCollection.
   */
  async deleteCustomTaxiway(icao: string, featureId: string): Promise<FeatureCollection> {
    const response = await api.delete(`/airports/${icao}/custom-taxiways/${featureId}`);
    return response.data as FeatureCollection;
  },

  /**
   * Upload an AIP AD2 PDF and extract vector topology via homography.
   * gcps: JSON-encoded list of 4 GCPs [{pdf:[x,y], wgs84:[lon,lat]}, ...] — optional for MPTO.
   */
  async importAipTopology(
    icao: string,
    file: File,
    gcps?: Array<{ pdf: [number, number]; wgs84: [number, number] }>,
    pageHint?: number,
  ): Promise<ImportTopologyResponse> {
    const form = new FormData();
    form.append('file', file);
    const params: Record<string, string> = {};
    if (gcps) params['gcps'] = JSON.stringify(gcps);
    if (pageHint !== undefined) params['page_hint'] = String(pageHint);
    const response = await api.post(`/airports/${icao}/import-aip-topology`, form, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // AIP PDF extraction can take up to 2 min on large files
    });
    return response.data as ImportTopologyResponse;
  },

  /** Return total page count of the uploaded AIP PDF. */
  async getAipPdfInfo(icao: string): Promise<{ page_count: number } | null> {
    try {
      const res = await api.get(`/airports/${icao}/aip-pdf/info`);
      return res.data as { page_count: number };
    } catch {
      return null;
    }
  },

  /** Fetch a single AIP PDF page as a blob URL (0-based page index). */
  async getAipPdfPageUrl(icao: string, pageNum: number, scale = 2): Promise<string | null> {
    try {
      const res = await api.get(`/airports/${icao}/aip-pdf/page/${pageNum}`, {
        responseType: 'blob',
        params: { scale },
        timeout: 30000,
      });
      return URL.createObjectURL(res.data as Blob);
    } catch {
      return null;
    }
  },

  /**
   * Upload an AIP/CAD PDF and render a preview PNG for the GCP picker.
   * Returns a blob URL for the image plus the PDF page dimensions in points.
   * pdfWidth/pdfHeight allow converting image click coords → PDF coords.
   */
  async previewAipPdfPage(
    icao: string,
    file: File,
    pageNum = 0,
    scale = 0.5,
  ): Promise<{ imageUrl: string; pdfWidth: number; pdfHeight: number } | null> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/airports/${icao}/aip-pdf/render-preview`, form, {
      params: { page_num: pageNum, scale },
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 60000,
    });
    const pdfWidth  = parseFloat((response.headers as Record<string, string>)['x-pdf-page-width']  ?? '0');
    const pdfHeight = parseFloat((response.headers as Record<string, string>)['x-pdf-page-height'] ?? '0');
    const imageUrl  = URL.createObjectURL(response.data as Blob);
    return { imageUrl, pdfWidth, pdfHeight };
  },

  /** Upload a GeoJSON FeatureCollection digitized in QGIS or similar. */
  async importGeoJson(icao: string, file: File): Promise<ImportTopologyResponse> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/airports/${icao}/import-geojson`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data as ImportTopologyResponse;
  },

  /**
   * Upload an AIXM 4.5 or 5.1 XML file and import aerodrome topology.
   */
  async importAixm(icao: string, file: File): Promise<ImportTopologyResponse> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/airports/${icao}/import-aixm`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as ImportTopologyResponse;
  },

  /**
   * Clear the imported topology (AIP PDF / GeoJSON / AIXM) and raster overlay for an airport.
   * OSM-derived taxiways and stands are NOT affected.
   */
  async resetImportedTopology(icao: string): Promise<void> {
    await api.delete(`/airports/${icao}/imported-topology`);
  },
};
